import type { z } from "zod";
export type OmitAndProhibit<T, K extends keyof T> = Omit<T, K> & Partial<Record<K, never>>;
export type Exact<TActual, TExpected> = TActual & Record<Exclude<keyof TActual, keyof TExpected>, never>;

/* Build factory method payload for a given entity. Omit fields from 'exclude', make optional those from 'optional' */
export type FactoryArgs<T, TSpec extends { exclude?: keyof T; optional?: keyof T } = Record<never, never>> = Omit<
  T,
  Extract<TSpec["exclude"], keyof T> | Extract<TSpec["optional"], keyof T>
> &
  Partial<Pick<T, Extract<TSpec["optional"], keyof T>>>;

export type NonEmptyArray<T> = [T, ...T[]];

/*
 * Lets `.map` on a NonEmptyArray return a NonEmptyArray. The `this` constraint only matches non-empty tuples,
 * so plain arrays fall back to the standard signature. Declared here so it is loaded wherever NonEmptyArray is.
 */
declare global {
  interface Array<T> {
    map<U>(
      this: NonEmptyArray<T>,
      callbackfn: (value: T, index: number, array: T[]) => U,
      thisArg?: unknown,
    ): NonEmptyArray<U>;
  }
  interface ReadonlyArray<T> {
    map<U>(
      this: Readonly<NonEmptyArray<T>>,
      callbackfn: (value: T, index: number, array: readonly T[]) => U,
      thisArg?: unknown,
    ): NonEmptyArray<U>;
  }
}

export const isEmptyArray = (arr: unknown[]): arr is [] => {
  return arr.length === 0;
};

export const isNonEmptyArray = <T>(arr: T[]): arr is NonEmptyArray<T> => {
  return arr.length > 0;
};

export type NonNullableFields<T> = { [P in keyof T]: NonNullable<T[P]> };

export const schemaForType =
  <T>() =>
  <S extends z.ZodType<T>>(arg: S) => {
    return arg;
  };

// Inspired from https://stackoverflow.com/questions/68411508/typescript-type-safe-string-with-dot-notation-for-query-nested-object
type PathImpl<T, K extends Extract<keyof T, string>> = K extends string
  ? T[K] extends Record<string, unknown>
    ? K | `${K}.${PathImpl<T[K], Extract<keyof T[K], string>>}`
    : K
  : never;

export type Path<T> = PathImpl<T, Extract<keyof T, string>> | Extract<keyof T, string>;

export type ValueOf<T> = T[keyof T];
